<template>
  <div class="flex flex-col justify-between flex-1">
    <div class="divide-y divide-gray-200">
      <div class="pt-2 pb-5 space-y-6">
        <Form :initial-values="initial_values" :validation-schema="schema" class="mt-2 space-y-4" @submit="createTask">
          <div class="flex flex-col">
            <div class="w-full mb-5">
              <SelectTemplateDropDownMenu
                v-if="templates.length"
                v-model="selected_template"
                :items="templates"
                label-attr="name"
                class="relative"
                :is-box-background="true"
                input-classes="sm:text-sm appearance-none h-10 font-medium text-gray-700 text-left block w-full px-3 py-2 w-48 rounded-lg shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
                @isEditTemplate="
                  ($event) => {
                    is_edit_template = $event
                  }
                " />
            </div>
            <div :class="{ flex: !inPlaybook }">
              <div :class="{ 'w-full': inPlaybook }" class="w-2/3 -space-y-px rounded-md shadow-sm">
                <TextInput
                  :label="$t('Task name')"
                  autocomplete="on"
                  :autocomplete-options="resultReferencesListForText"
                  name="name"
                  type="text"
                  :auto-focus="true" />
              </div>
              <div :class="{ 'h-24 mt-2 w-[800px] ml-20': inPlaybook }" class="w-1/2 h-0 ml-6 -mt-6">
                <div v-if="isAllRecords" class="mt-8 whitespace-nowrap text-common-grey">
                  {{ count - excludedRecords.length }} {{ $t("out of") }} {{ count }} {{ $t("records in table selected") }}
                </div>
                <CompaniesSelect
                  v-else
                  :multi-select="inPlaybook ? true : false"
                  :short-king="inPlaybook"
                  :open-position="inPlaybook ? 'right' : 'center'"
                  :label="$t('Add Companies/Opportunities')"
                  :companies="selected_companies"
                  :autocomplete-options="resultReferencesList"
                  @changed="
                    ($event) => {
                      handleSetCompaniesList($event)
                    }
                  " />
              </div>
            </div>
          </div>

          <div class="flex w-full">
            <div class="w-1/2 mt-3 mb-5">
              <TeamMembersSelect
                v-model:selected-team-members="selected_users"
                :autocomplete-options="autocompleteOptionsInPlaybook"
                :team-members="selected_users"
                :label="$t('Add Team Members')" />
            </div>
            <div v-if="!inPlaybook" class="w-1/2 mt-3 mb-5">
              <ContactsSelect v-model:selected-contacts="selected_contacts" :label="$t('Contacts')" :companies="selected_companies" />
            </div>
          </div>

          <div class="mt-5 border rounded-md dark:border-dark-divider-border h-28">
            <TextEditor
              ref="quill"
              v-model:content="task_description"
              name="description"
              :label="$t('Description')"
              content-type="html"
              input-classes="rounded-md"
              :value="initial_values?.description"
              :mention-items="resultReferencesListForText"
              theme="bubble"
              toolbar="essential" />
          </div>

          <div class="flex flex-row justify-between gap-2">
            <div class="relative w-full">
              <label
                for="start_date"
                class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                :class="[
                  inputStartDateIsFocused || !!start_date ? 'top-0' : 'top-1/2',
                  isBoxBackground
                    ? 'bg-light-box-background dark:bg-dark-box-background'
                    : 'bg-white text-gray-400 dark:bg-dark-box-background',
                  inputStartDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                ]">{{ $t("Start date") }}</label>
              <div class="rounded-md shadow-sm">
                <DatePicker
                  ref="start_date"
                  v-model="start_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  :attributes="[
                    {
                      key: 'today',
                      highlight: {
                        color: 'gray',
                        fillMode: 'light'
                      },
                      dates: new Date()
                    }
                  ]"
                  mode="date"
                  :popover="{ visibility: 'click' }">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        id="start_date"
                        :value="inputValue"
                        autocomplete="off"
                        class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackground
                            ? 'bg-light-box-background dark:bg-dark-box-background'
                            : 'bg-light-screen-background dark:bg-dark-box-background'
                        ]"
                        @focusin="focusStartDateInput"
                        @focusout="focusStartDateInput"
                        v-on="inputEvents"
                        @input="
                          $utils.debounce(() => {
                            start_date = $event.target?.value
                          }, 800)
                        ">
                    </div>
                  </template>
                  <template #footer>
                    <div class="flex items-center justify-end w-full pb-2 pr-2 mt-0">
                      <button v-if="start_date" type="button" class="cancel-btn inline-block" @click.stop="emptyStartDate">
                        {{ $t("Clear") }}
                      </button>
                      <button type="button" class="inline-block cancel-btn" @click.stop="clearDate">{{ $t("Cancel") }}</button>
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
            <div class="relative w-full">
              <label
                for="create_task_d_date"
                class="absolute z-10 block px-1 text-sm transition-all ease-in-out -translate-y-1/2 rounded-full h-fit dark:text-dark-search-bar-text dark:text-dark-body-text left-2 duration-side-menu-transition-duration"
                :class="[
                  !!relative_date_text || inputDueDateIsFocused || !!due_date ? 'top-0' : 'top-1/2',
                  isBoxBackground
                    ? 'bg-light-box-background dark:bg-dark-box-background'
                    : 'bg-white text-gray-400 dark:bg-dark-box-background',
                  inputDueDateIsFocused ? 'text-light-brand-blue dark:text-dark-brand-blue' : ''
                ]"
                @click="is_modal_relative_date_open = true">{{ $t("Due date") }}</label>
              <div class="rounded-md shadow-sm">
                <DatePicker
                  v-model="due_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="bg-white dark:bg-dark-screen-background"
                  is-inline
                  :attributes="[
                    {
                      key: 'today',
                      highlight: {
                        color: 'gray',
                        fillMode: 'light'
                      },
                      dates: new Date()
                    }
                  ]"
                  mode="date"
                  :popover="{ visibility: 'none' }">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        id="create_task_d_date"
                        :value="inputValue ? inputValue : relative_date_text"
                        autocomplete="off"
                        class="w-full h-10 px-10 pl-8 pr-2 transition ease-in-out border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackground
                            ? 'bg-light-box-background dark:bg-dark-box-background'
                            : 'bg-light-screen-background dark:bg-dark-box-background'
                        ]"
                        @focusin="focusDueDateInput"
                        @focusout="focusDueDateInput"
                        @click="is_modal_relative_date_open = true"
                        v-on="inputEvents"
                        @input="
                          $utils.debounce(() => {
                            due_date = $event.target?.value
                          }, 800)
                        ">
                    </div>
                    <div v-if="is_modal_relative_date_open" class="absolute z-20">
                      <DateTimeFrame
                        ref="due_date"
                        class="w-full mr-2 bg-white rounded-lg dark:bg-dark-box-background"
                        @done="
                          ($event) => {
                            due_date = $event
                            is_modal_relative_date_open = false
                          }
                        "
                        @relative-date-changed="
                          ($event) => {
                            relative_date = $event
                            is_modal_relative_date_open = false
                          }
                        "
                        @cancel="is_modal_relative_date_open = false" />
                    </div>
                  </template>

                  <template #footer>
                    <div class="flex items-center justify-end w-full pb-2 pr-2 mt-0">
                      <button v-if="due_date || relative_date" type="button" class="cancel-btn inline-block" @click.stop="emptyDueDate">
                        {{ $t("Clear") }}
                      </button>
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>

            <SelectInput
              name="priority"
              :items="task_priority"
              :value="initial_values.priority"
              label-attr="name"
              :allow-null="false"
              :classes="'w-1/2'"
              value-attr="value"
              :label="$t('Priority')"
              :show-label="true"
              :is-box-background="true"
              input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" />
          </div>

          <div :class="[{ '!mt-6': inPlaybook }]" class="flex items-center mt-0 mr-6 gap-x-2">
            <Switch
              v-model="auto_set_start_date"
              :class="[
                auto_set_start_date ? 'bg-blue' : 'bg-gray-200',
                'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
              ]">
              <span
                aria-hidden="true"
                :class="[
                  auto_set_start_date && $store.state.portfolio.ui_options?.language == 'ar'
                    ? '-translate-x-5'
                    : auto_set_start_date
                      ? 'translate-x-5'
                      : 'translate-x-0',
                  'inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                ]" />
            </Switch>
            <span class="dark:text-common-grey">{{ $t("Auto Set Start Date") }}</span>
          </div>

          <div class="relative rounded-md shadow-sm border p-2 mt-2">
            <div class="flex items-center justify-between w-full">
              <label class="block text-common-grey">{{ $t("Tags") }}</label>
              <button class="primary-btn w-fit" @click.prevent="is_tags_modal_open = true">
                <Plus class="w-6 h-6 mr-2" aria-hidden="true" />
                <span>{{ selected_tags.length ? $t("Edit Tags") : $t("Add Tags") }}</span>
              </button>
            </div>
            <div v-if="selected_tags.length" class="flex flex-wrap gap-2 mt-4">
              <div
                v-for="tag in selected_tags"
                :key="tag?.id"
                class="flex focus:outline-none rounded-md"
                :style="{ background: tag.color }">
                <div v-if="selected_tags.find((t) => t.id == tag.id)" class="flex items-center group">
                  <span
                    class="flex items-center rounded-md px-2 py-1.5 border border-gray-300"
                    :style="{
                      background: tag.color
                    }">
                    <span class="block text-white truncate">{{ tag.name }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <AttachmentsField v-model="attachments" class="mt-4 mb-6" />

          <div class="flex flex-row justify-between w-full gap-2 pt-12">
            <button type="button" class="w-1/2 m-0 cancel-btn" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
            <button v-if="!is_edit_template"
                    ref="task-create"
                    :disabled="submitting_is_on"
                    type="submit"
                    class="w-1/2 primary-btn">
              <RotateCw
                :class="{
                  'animate-spin': submitting_is_on,
                  'opacity-100': submitting_is_on
                }"
                class="w-5 h-5 mr-3 -ml-1 opacity-0"
                aria-hidden="true" />

              {{ inPlaybook ? $t("Save") : $t("Create") }} {{ $t("Task") }}
            </button>
            <button v-else ref="task-create" type="submit" class="w-1/2 primary-btn">{{ $t("Update template") }}</button>
          </div>
        </Form>
      </div>
    </div>
  </div>

  <!-- Add tags -->
  <teleport to="body">
    <Modal v-model:is-open="is_tags_modal_open" :default-success-msg="$t('Tags Updated')">
      <template #form="{ close: closeModal }">
        <TagsAutoCompleteInput
          :placeholder="$t('Add new tag')"
          :selected-tags="selected_tags"
          @changed="
            ($event) => {
              handleTagsChanged($event)
            }
          "
          @close="closeModal" />
      </template>
    </Modal>
  </teleport>
</template>

<script>
import { Switch } from "@headlessui/vue"
import * as Yup from "yup"
import Modal from "@/components/shared/Modal.vue"
import { DatePicker } from "v-calendar"
import TextInput from "@/components/shared/TextInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import { Form } from "vee-validate"
import SelectTemplateDropDownMenu from "@/components/shared/SelectTemplateDropDownMenu.vue"
import DateTimeFrame from "@/components/shared/DateTimeFrame.vue"
import ContactsSelect from "@/components/shared/ContactsSelect.vue"
import TeamMembersSelect from "@/components/shared/TeamMembersSelect.vue"
import TextEditor from "@/components/shared/TextEditor.vue"
import CompaniesSelect from "@/components/shared/CompaniesSelect.vue"
import dataService from "@/services/data.service"
import AttachmentsField from "@/components/shared/form-inputs/AttachmentsField.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { Plus, RotateCw } from "lucide-vue-next"
import TagsAutoCompleteInput from "@/components/shared/TagsAutoCompleteInput.vue"
import { event as google_event } from "vue-gtag-next"

export default {
  components: {
    Switch,
    Plus,
    Modal,
    TagsAutoCompleteInput,
    TextEditor,
    SelectTemplateDropDownMenu,
    DateTimeFrame,
    TextInput,
    DatePicker,
    SelectInput,
    Form,
    ContactsSelect,
    TeamMembersSelect,
    CompaniesSelect,
    AttachmentsField,
    RotateCw
  },
  props: {
    isEditTemplate: {
      type: Boolean,
      default: false
    },
    selectedTemplate: {
      type: Object,
      default: null
    },
    resultReferencesList: null,
    resultReferencesListForText: null,
    autocompleteOptionsInPlaybook: {
      type: Array,
      required: false,
      default: () => []
    },
    inPlaybook: {
      type: Boolean,
      default: false
    },
    record: {
      type: Object,
      required: false
    },
    records: {
      type: Array,
      required: false
    },
    isInPlan: {
      type: Boolean,
      required: false
    },
    isInGoal: {
      type: Boolean,
      required: false
    },
    isInInteraction: {
      type: Boolean,
      required: false
    },
    isInMilestone: {
      type: Boolean,
      required: false
    },
    contact: {
      type: Object,
      required: false
    },
    isAllRecords: {
      type: Boolean,
      default: false
    },
    excludedRecords: {
      type: Array,
      default: () => []
    },
    objectType: {
      type: String,
      required: false
    },
    // comes from AI suggested tasks
    defaults: {
      type: Object,
      default: () => {},
      required: false
    },
    // autocompleteOptions: {
    //   type: Array,
    //   required: false
    // },
    count: null
  },

  emits: ["cancel", "submitted", "error", "done"],

  data() {
    return {
      initial_values: {
        name: "",
        description: "",
        priority: "normal"
      },
      task_priority: [
        { name: this.$t("Lowest"), value: "lowest" },
        { name: this.$t("Low"), value: "low" },
        { name: this.$t("Normal"), value: "normal" },
        { name: this.$t("High"), value: "high" },
        { name: this.$t("Highest"), value: "highest" }
      ],
      selected_tags: [],
      all_tags: [],
      is_tags_modal_open: false,
      is_edit_template: false,
      attachments: [],
      selected_users: [],
      selected_contacts: [],
      selected_template: null,
      templates: [],
      task_description: null,
      due_date: null,
      relative_date: null,
      relative_date_text: null,
      start_date: null,
      inputDueDateIsFocused: false,
      inputStartDateIsFocused: false,
      is_modal_relative_date_open: false,
      selected_companies: [],
      isBoxBackground: false,
      submitting_is_on: false,
      auto_set_start_date: false,
      schema: null
    }
  },
  watch: {
    relative_date() {
      if (this.relative_date) {
        if (this.relative_date.due_date_relative_to == "datetime_created") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Creation Date")
        } else if (this.relative_date.due_date_relative_to == "start_date") {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days after Start Date")
        } else {
          this.relative_date_text = `${this.relative_date.due_date_days_offset} ` + this.$t("days from In Progress")
        }
        this.due_date = null
      }
    },
    due_date() {
      if (this.due_date) {
        this.relative_date_text = null
      }
    },
    selected_template() {
      this.relative_date_text = null
      if (this.selected_template?.content?.due_date_relative_to) {
        this.relative_date = {
          due_date_relative_to: this.selected_template.content.due_date_relative_to,
          due_date_days_offset: this.selected_template.content.due_date_days_offset
        }
      } else {
        this.due_date = this.selected_template?.content?.due_date
      }
      this.start_date = this.selected_template?.content?.start_date
      if (this.selected_template?.content?.attachments?.length) {
        this.attachments = this.selected_template?.content?.attachments
      }
      if (this.selected_template?.content?.tags && this.selected_template?.content?.tags.length) {
        this.selected_tags = this.$store.state.data.actions_tags.filter((tag) => this.selected_template.content.tags.includes(tag.id))
      }

      if (this.selected_template?.content?.assigned_to?.length) {
        this.selected_users = this.selected_template?.content?.assigned_to
      }

      if (this.selected_template?.content?.record) {
        if (this.selected_template?.content?.record.id) {
          this.selected_companies.push(this.selected_template?.content?.record)
        } else {
          this.$store.dispatch("data/getRecord", this.selected_template?.content?.record).then((res) => {
            this.selected_companies.push(res.data)
          })
        }
      }
      if (this.selected_template?.content?.auto_start_date_set) {
        this.auto_set_start_date = this.selected_template?.content?.auto_start_date_set

        if (this.selected_template?.content.status == "in-progress" && !this.selected_template?.content.start_date) {
          this.start_date = new Date()
        }
      }

      if (this.selected_template?.content?.contacts?.length) {
        this.selected_template?.content?.contacts.forEach((c) => {
          if (c.id) {
            this.selected_contacts.push(c)
          } else {
            this.$store.dispatch("data/getRecord", c).then((res) => {
              this.selected_contacts.push(res.data)
            })
          }
        })
      }
      this.initial_values = this.getInitialValues()
    }
  },

  created() {
    if (this.selectedTemplate && this.isEditTemplate) {
      this.is_edit_template = true
      this.selected_template = this.selectedTemplate
    } else if (this.selectedTemplate) {
      this.is_edit_template = false
      this.selected_template = this.selectedTemplate
    }
    const params = { source_object_type: "task" }
    dataService.getTemplates(params).then((res) => {
      this.templates = res.data.results
    })
    if (!this.isEditTemplate) {
      this.selected_users.push(this.$store.state.auth.user)
    }
    if (this.isInInteraction) {
      this.selected_contacts = this.contact ? this.contact : []
    } else {
      this.selected_contacts = this.contact ? [this.contact] : []
    }

    if (this.$route.name !== "WorkspaceItem") {
      this.records?.slice(0, 20)?.forEach((rec) => {
        if (rec?.id) {
          this.selected_companies.push(rec)
        } else {
          if (this.$store.state.data.records_fetched[rec]) {
            this.selected_companies.push(this.$store.state.data.records_fetched[rec])
          } else {
            this.$store.dispatch("data/getRecord", rec).then((res) => {
              this.selected_companies.push(res.data)
            })
          }
        }
      })
    }

    if (this.record && this.$route.name !== "WorkspaceItem") {
      if (this.record.id) {
        this.selected_companies.push(this.record)
      } else {
        if (this.$store.state.data.records_fetched[this.record]) {
          this.selected_companies.push(this.$store.state.data.records_fetched[this.record])
        } else {
          this.$store.dispatch("data/getRecord", this.record).then((res) => {
            this.selected_companies.push(res.data)
          })
        }
      }
    }

    if (this.$route.name == "WorkspaceItem") {
      this.$store.dispatch("data/getRecord", this.$store.state.ui.currentWorkspace.record).then((res) => {
        this.selected_companies.push(res.data)
      })
    }
    this.schema = Yup.object().shape({
      name: Yup.string().required(this.$t("Task name is required")),
      description: Yup.string().nullable(),
      goal: Yup.string().nullable(),
      // record: Yup.string().required(),
      assigned_to: Yup.array().of(Yup.string())
    })
    this.initial_values = this.getInitialValues()
    // see if this.defaults?.["due_date"] is a number
    if (this.defaults?.["due_date"]) {
      this.relative_date = {
        due_date_relative_to: "datetime_created",
        due_date_days_offset: Number(this.defaults?.["due_date"])
      }
      this.relative_date_text = `${this.defaults?.["due_date"]} ` + this.$t("days after Creation Date")
    }
    if (this.defaults?.record) {
      this.selected_companies.push(this.defaults?.record)
    }
  },

  methods: {
    getInitialValues() {
      return {
        name: this.getInitialDefaultNameValue(),
        description:
          this.defaults?.["description"] || this.selected_template?.content?.description ? this.selected_template.content.description : "",
        priority:
          this.defaults?.["priority"] || this.selected_template?.content?.priority ? this.selected_template.content.priority : "normal",
        record: this.record ? this.record.id : null
      }
    },
    getInitialDefaultNameValue() {
      return this.defaults?.["name"]
        ? this.defaults?.["name"]
        : this.selected_template?.content?.name && this.is_edit_template
          ? this.selected_template.name
          : this.selected_template?.content?.name
            ? this.$t("Copy of") + ` ${this.selected_template.content.name}`
            : ""
    },
    emptyStartDate() {
      this.start_date = null
      this.$refs.start_date.hidePopover()
    },
    emptyDueDate() {
      this.due_date = null
      this.relative_date = null
      this.relative_date_text = null
      this.$refs.due_date.hidePopover()
      this.is_modal_relative_date_open = false
    },
    handleTagsChanged(tags) {
      this.all_tags = this.$store.state.data.actions_tags
      this.selected_tags = tags
    },
    handleSetCompaniesList(e) {
      this.selected_companies = e
    },
    focusStartDateInput() {
      this.inputStartDateIsFocused = !this.inputStartDateIsFocused
    },
    focusDueDateInput() {
      this.inputDueDateIsFocused = !this.inputDueDateIsFocused
    },
    clearDate() {
      this.$refs.start_date.hidePopover()
    },
    createTask(values) {
      this.submitting_is_on = true
      let data = { ...values }
      data["description"] = this.task_description
      data["assigned_to"] = this.selected_users.map((x) => (x.id ? x.id : x))
      data["contacts"] = this.selected_contacts.map((x) => (x.id ? x.id : x))
      data["tags"] = this.selected_tags.map((t) => t.id)

      data["auto_start_date_set"] = this.auto_set_start_date

      if (this.start_date) {
        data["start_date"] = data["start_date"] = this.$moment(this.start_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
      } else {
        data["start_date"] = null
      }
      if (this.relative_date) {
        data["due_date_relative_to"] = this.relative_date.due_date_relative_to
        data["due_date_days_offset"] = this.relative_date.due_date_days_offset
      } else if (this.due_date) {
        data["due_date"] = data["due_date"] = this.$moment(this.due_date)
          .set("hour", this.$moment().hour())
          .set("minute", this.$moment().minute())
          .set("second", this.$moment().second())
          .toISOString()
      } else {
        data["due_date"] = null
        data["due_date_relative_to"] = null
        data["due_date_days_offset"] = 0
      }
      if (this.is_edit_template) {
        //Edit template

        if (this.selected_companies.length) {
          data["record"] = this.selected_companies[0].id ? this.selected_companies[0].id : this.selected_companies[0]
        }

        let data_template = { ...this.selected_template.content, ...data }
        dataService
          .updateTemplate(this.selected_template.id, {
            name: data.name,
            content: data_template
          })
          .then((res) => {
            this.$emitter.emit(ALL_EMITTERS.TEMPLATE_UPDATED, res.data)
            this.$emit("done")
          })
      } else {
        //Create new task
        let params = {
          expand: "record,contacts"
        }

        const post = {
          task: { ...data },
          records_full: this.selected_companies,
          records: this.selected_companies.map((c) => c.id),
          params: params,
          is_all_records: this.isAllRecords,
          excluded_records: this.excludedRecords,
          object_type: this.objectType
        }

        if (this.inPlaybook) {
          data["companies"] = this.selected_companies.map((c) => (c.id ? c.id : c))
          data["status"] = "pending"

          if (this.attachments.length) {
            const new_attachments = this.attachments.filter((att) => typeof att.file !== "string")
            const old_attachments = this.attachments.filter((att) => typeof att.file == "string")

            let attachment_calc = old_attachments.map((att) => att.id)

            if (new_attachments.length) {
              dataService.addAttachment({ attachments: new_attachments }).then((res) => {
                attachment_calc = attachment_calc.concat(res.data.map((a) => a.id))
                data["attachments"] = attachment_calc
                this.$emit("done", data)
              })
            } else {
              data["attachments"] = attachment_calc
              this.$emit("done", data)
            }
          } else {
            this.$emit("done", data)
          }
          google_event("create_task_click", {
            event_label: "Create new task",
            page: "Playbooks",
            name: "Playbooks",
            user_name: this.$store.state.auth.user.full_name,
            user_email: this.$store.state.auth.user.email,
            organization: this.$store.state.organization.name
          })
        } else {
          this.$store
            .dispatch("data/bulkTaskCreate", { task: post })
            .then((res) => {
              if (this.isInPlan) {
                this.$emitter.emit(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_PLAN_CREATE_FORM, res)
                this.$emitter.emit(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_PLAN_UPDATE_FORM, res)
              }
              if (this.isInGoal) {
                this.$emitter.emit(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_GOAL_CREATE_FORM, res)
                this.$emitter.emit(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_GOAL_UPDATE_FORM, res)
              }
              if (this.isInInteraction) {
                this.$emitter.emit(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_INTERACTION_CREATE_FORM, res)
                this.$emitter.emit(ALL_EMITTERS.ADD_SELECTED_TASKS_TO_INTERACTION_UPDATE_FORM, res)
              }
              if (this.attachments.length) {
                let formData = new FormData()
                this.attachments.forEach((attachment, i) => {
                  Object.keys(attachment).forEach((field) => formData.append(`attachments[${i}][${field}]`, attachment[field]))
                })
                this.$emitter.emit(ALL_EMITTERS.NEW_ACTINON_CREATED_OR_UPDATED, this.due_date ? new Date(this.due_date) : null)

                res.forEach((task) => {
                  this.$store.dispatch("data/addAttachmentsToTask", {
                    id: task.id,
                    attachments: formData,
                    record_full: this.selected_companies.find((c) => c.id == task.record)
                  })
                })
              }

              this.$emit("done", res[0])
              if (this.isInMilestone) {
                this.$emitter.emit(ALL_EMITTERS.ADD_TASK_TO_MILESTONE_CREATE_FORM, res[0])
              }
              this.submitting_is_on = false
            })
            .catch(() => {
              this.submitting_is_on = false

              // To do show exceptions
              this.$emit("error")
            })
        }
      }
    }
  }
}
</script>
